import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, boxesIntersect, useSelectionContainer } from '@air/react-drag-to-select';

type TimeOfDayFieldProps<C, R> = {
    columns: C[],
    rows: R[],
    selected: boolean[][],
    onChange: (newSelected: boolean[][]) => void,
    colDisplay: (col: C) => string,
    rowDisplay: (row: R) => string,
    isDisabled?: boolean,
};

export function TimeOfDayField<C, R>(props: TimeOfDayFieldProps<C, R>) {
    const blankArray = useMemo(() => props.columns.map(() => props.rows.map(() => false)), [props.columns, props.rows]);

    const [dragSelections, setDragSelections] = useState<boolean[][]>(blankArray);
    const selectableItems = useRef<{ col: number, row: number, box: Box }[]>([]);
    const elementsContainerRef = useRef<HTMLDivElement | null>(null);

    const { DragSelection } = useSelectionContainer({
        eventsElement: elementsContainerRef.current,
        shouldStartSelecting: (target) => {
            if (target instanceof HTMLElement) {
                const col = target.getAttribute('data-col');
                const row = target.getAttribute('data-row');
                return col !== null && row !== null;
            }
            return false;
        },
        onSelectionChange: (box) => {
            const scrollAwareBox: Box = {
                ...box,
                top: box.top + window.scrollY,
                left: box.left + window.scrollX
            };
            const newArray = [...blankArray.map(it => [...it])];
            selectableItems.current.filter(it => boxesIntersect(scrollAwareBox, it.box)).forEach(item => newArray[item.col][item.row] = true);
            setDragSelections(newArray);
        },
        onSelectionStart: (event) => {
            setDragSelections(blankArray);
            if (elementsContainerRef.current) {
                selectableItems.current = Array.from(elementsContainerRef.current.children).flatMap((item) => {
                    const col = item.getAttribute('data-col');
                    const row = item.getAttribute('data-row');
                    if (col && row) {
                        const { left, top, width, height } = item.getBoundingClientRect();
                        return [{
                            col: parseInt(col),
                            row: parseInt(row),
                            box: {
                                left,
                                top,
                                width,
                                height
                            }
                        }];
                    } else return [];
                });
            }
        },
        onSelectionEnd: () => {
            if (dragSelections.flatMap(it => it).reduce((a, b) => a + (b ? 1 : 0), 0) > 1) {
                let newSelected = [...props.selected.map(it => [...it])];
                let switchToSelected = false;
                dragSelections.forEach((col, i) => col.forEach((present, j) => {
                    if (present && !newSelected[i][j]) switchToSelected = true;
                }));
                dragSelections.forEach((col, i) => col.forEach((present, j) => {
                    if (present) newSelected[i][j] = switchToSelected;
                }));
                props.onChange(newSelected);
            }
            setDragSelections(blankArray);
        },
        selectionProps: { hidden: true },
        isEnabled: !props.isDisabled
    });

    const flipCell = useCallback((col: number, row: number) => {
        let newSelected = [...props.selected.map(it => [...it])];
        newSelected[col][row] = !newSelected[col][row];
        props.onChange(newSelected);
    }, [props.selected, props.onChange]);

    const completeCols = useMemo(() => {
        return props.selected.map(it => !it.includes(false));
    }, [props.selected]);
    const flipCol = useCallback((col: number) => {
        let newSelected = [...props.selected];
        const del = completeCols[col];
        for (let j = 0; j < newSelected[col].length; j++) newSelected[col][j] = !del;
        props.onChange(newSelected);
    }, [props.selected, props.onChange, completeCols]);

    const completeRows = useMemo(() => {
        return props.rows.map((_, j) => {
            for (let i = 0; i < props.columns.length; i++) {
                if (!props.selected[i][j]) return false;
            }
            return true;
        });
    }, [props.selected]);
    const flipRow = useCallback((row: number) => {
        let newSelected = [...props.selected];
        const del = completeRows[row];
        for (let i = 0; i < props.columns.length; i++) newSelected[i][row] = !del;
        props.onChange(newSelected);
    }, [props.selected, props.onChange, completeRows]);

    const selectedColourLight = props.isDisabled ? 'bg-gray-50' : 'bg-blue-50';
    const selectedColourDark = props.isDisabled ? 'bg-gray-600' : 'bg-blue-600';

    return <div
        style={{ display: 'grid' }}
        ref={elementsContainerRef}
    >
        <DragSelection />
        {props.rows.map((row, rowI) =>
            <div
                key={`${rowI}:`}
                style={{ gridColumn: 1, gridRow: rowI + 2 }}
                className={'h-8 p-0.5 flex items-center justify-center outline outline-gray-200 ' + (completeRows[`${rowI}`] ? selectedColourLight : '')}
                onClick={() => {
                    if (!props.isDisabled) flipRow(rowI);
                }}
            >
                {props.rowDisplay(row)}
            </div>
        )}
        {props.columns.map((col, colI) =>
            <div
                key={`${colI}:`}
                style={{ gridColumn: colI + 2, gridRow: 1, writingMode: 'vertical-lr' }}
                className={'p-0.5 flex items-center justify-center outline outline-gray-200 ' + (completeCols[`${colI}`] ? selectedColourLight : '')}
                onClick={() => {
                    if (!props.isDisabled) flipCol(colI);
                }}
            >
                {props.colDisplay(col)}
            </div>
        )}
        {props.rows.flatMap((row, rowI) => props.columns.map((col, colI) =>
            <div
                key={`${rowI}:${colI}`}
                style={{ gridColumn: colI + 2, gridRow: rowI + 2 }}
                className={'outline outline-gray-200 ' + (dragSelections[colI][rowI] ? 'bg-blue-300' : (props.selected[colI][rowI] ? selectedColourDark : ''))}
                onClick={() => {
                    if (!props.isDisabled) flipCell(colI, rowI);
                }}
                data-col={colI}
                data-row={rowI}
            />
        ))}
    </div>;
}
